import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

const TimerBar = ({ duration, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState(duration)
  const [progress, setProgress] = useState(100)
  const [barColor, setBarColor] = useState('bg-green-500')
  const timerRef = useRef(null)

  useEffect(() => {
    setTimeLeft(duration)
    setProgress(100)
    setBarColor('bg-green-500')
    // Clear any existing interval when duration changes (new card)
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }, [duration])

  useEffect(() => {
    if (timeLeft <= 0) {
      if (timerRef.current) clearInterval(timerRef.current)
      onComplete()
      return
    }

    // Only set interval if timeLeft > 0
    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 0.1)
    }, 100)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [timeLeft, onComplete]) // Removed duration from here as it's handled in the first useEffect

  useEffect(() => {
    const newProgress = duration > 0 ? (timeLeft / duration) * 100 : 0
    setProgress(Math.max(0, newProgress))

    if (newProgress <= 25) {
      setBarColor('bg-red-500')
    } else if (newProgress <= 50) {
      setBarColor('bg-yellow-500')
    } else {
      setBarColor('bg-green-500')
    }
  }, [timeLeft, duration])

  const formatTime = (timeInSeconds) => {
    const totalSeconds = Math.ceil(timeInSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full my-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Time Remaining:</span>
        <span className="text-xs font-semibold text-primary dark:text-primary-300">
          {formatTime(timeLeft)}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-2.5 rounded-full transition-colors ease-linear duration-150 ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}

TimerBar.propTypes = {
  duration: PropTypes.number.isRequired,
  onComplete: PropTypes.func.isRequired,
}

export default TimerBar
